// src/pages/HomePage.js
import React, { useState, useEffect, useRef, useContext  } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getExams, getTopExams, postSearchKeyword } from '../services/api';
import { Container, Title, Input, List, ListItem, NoResultsMessage } from '../styles/StyledComponents';
import { SquareCard, CardImage, CardTitle, MovingCardsContainer, MovingCardsInner } from '../styles/StyledComponents';
import RotatingTitle from '../components/RotatingTitle';
import Footer from '../components/Footer';
import FeaturedSections from '../components/FeaturedSections';
import { LocationContext } from '../App';

const HomePage = () => {
  const { userLocation, setUserLocation } = useContext(LocationContext);
  const { country } = useParams();
  const [currentLocation, setCurrentLocation] = useState(userLocation);
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [topExams, setTopExams] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const initialRender = useRef(true);

  // Handle region from URL
  useEffect(() => {
    // Extract region from URL path if present
    if (country) {
      const region = country.toUpperCase();
      setUserLocation(region); // Update the context
      setCurrentLocation(region);
    } else {
      setCurrentLocation(userLocation);
      // Redirect to the region-specific URL
      navigate(`/region/${userLocation.toLowerCase()}`, { replace: true });
    }
  }, [country, userLocation, setUserLocation, navigate]);
  
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      fetchExams();
      fetchTopExams();
    }
  }, []);
  const [isLoading, setIsLoading] = useState(true);

  const fetchExams = async () => {
    setIsLoading(true);
    try {
      const response = await getExams();
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopExams = async () => {
    try {
      const response = await getTopExams(); // Assuming you have this API function
      setTopExams(response.data);
    } catch (error) {
      console.error('Error fetching top exams:', error);
      // Optionally set some default data if the fetch fails
      setTopExams([
      ]);
    }
  };

  const filteredExams = exams.filter(exam =>
    exam?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const duplicatedExams = [...exams, ...exams];

  //This call is to capture the search term from the frontend so that we can add mostly requested exams and implement them.
  const triggerBackendSearch = async (term) => {
    setHasSearched(true);
    try {
      const response = await postSearchKeyword({ keyword: term });
      // const response = await axios.post('/api/exams/search', { keyword: term });
      console.log(`Search for "${term}" recorded. Hit count: ${response.data.hitCount}`);
    } catch (error) {
      console.error('Error recording search:', error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (hasSearched) {
      setHasSearched(false);
    }
  };

    // Generate meta description based on available exams
    const generateMetaDescription = () => {
      if (topExams && topExams.length > 0) {
        const examNames = topExams.slice(0, 3).map(exam => exam.title).join(', ');
        return `Prepare for competitive exams with MockTestPro. Practice with mock tests for ${examNames} and more. Improve your skills with timed tests and detailed explanations.`;
      }
      return 'Prepare for competitive exams with MockTestPro. Practice with our comprehensive collection of mock tests. Improve your skills with timed tests and detailed explanations.';
    };

  return (
    <>
    <Helmet>
      <title>MockTestPro | Practice Tests for Competitive Exams</title>
      <meta name="description" content={generateMetaDescription()} />
      <meta name="keywords" content="mocktestpro, mock test pro, mock tests, practice exams, competitive exams, online tests, exam preparation" />
      <link rel="canonical" href={`https://mocktestpro.com/region/${currentLocation.toLowerCase()}`} />
      <link rel="alternate" hrefLang="en-us" href="https://mocktestpro.com/region/us" />
      <link rel="alternate" hrefLang="en-in" href="https://mocktestpro.com/region/in" />
      <link rel="alternate" hrefLang="x-default" href="https://mocktestpro.com/region/us" />
      <meta property="og:title" content="MockTestPro | Practice Tests for Competitive Exams" />
      <meta property="og:description" content={generateMetaDescription()} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://mocktestpro.com/region/${currentLocation.toLowerCase()}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="MockTestPro | Practice Tests for Competitive Exams" />
      <meta name="twitter:description" content={generateMetaDescription()} />
    </Helmet>
    <div style={{
      backgroundImage: `url(/images/bg-image.jpg)`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      minHeight: '100vh',
       padding: '20px'
    }}>
    <Container>
      <RotatingTitle />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '800px', margin: '0 auto' }}>
      <Input
        type="text"
        placeholder="Search exams..."
        value={searchTerm}
        onChange={handleSearchChange}
        style={{ borderColor: '#3498db', color: '#2c3e50' }}
      />

      <div style={{ height: '200px', overflowY: 'auto' }}>
        {searchTerm ? (
          filteredExams.length > 0 ? (
            <List>
              {filteredExams.map((exam) => (
                <ListItem key={exam.examId}>
                  <Link to={`/exam/${exam.examId}`} style={{ color: '#3498db', textDecoration: 'none' }}>{exam.title}</Link>
                </ListItem>
              ))}
            </List>
          ) : (
            hasSearched ? (
              <NoResultsMessage style={{ color: '#7f8c8d' }}>
                <p>We couldn't find any exams matching your search.</p>
                <p>Don't worry! We're constantly working on adding new exams to our collection.</p>
                <p>Please check back soon or try a different search term.</p>
              </NoResultsMessage>
            ) : (
              <List>
                <ListItem onClick={() => triggerBackendSearch(searchTerm)}>
                  <span style={{ color: '#3498db', cursor: 'pointer' }}>
                    Explore all available exams for "{searchTerm}" - Click to expand your search
                  </span>
                </ListItem>
              </List>
            )
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '800px', margin: '0 auto' }}>
            <h3 style={{ color: '#3498db', marginBottom: '10px' }}>Most Popular Exams This Month</h3>
            <List>
              {topExams.map((exam) => (
                <ListItem key={exam.examId}>
                  <Link to={`/exam/${exam.examId}`} style={{ color: '#3498db', textDecoration: 'none' }}>{exam.title}</Link>
                </ListItem>
              ))}
            </List>
          </div>
        )}
      </div>
      </div>

      {/* Add Featured Sections Component here */}
      <FeaturedSections />

      <MovingCardsContainer>
        <MovingCardsInner>
          {duplicatedExams.map((exam, index) => (
            <Link to={`/exam/${exam.examId}`} key={`${exam.examId}-${index}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <SquareCard style={{ backgroundColor: '#ecf0f1', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <CardImage src={exam.imageUrl || '/images/mockTest256x256.png'} alt={exam.title} />
                <CardTitle style={{ color: '#2c3e50' }}>{exam.title}</CardTitle>
              </SquareCard>
            </Link>
          ))}
        </MovingCardsInner>
      </MovingCardsContainer>
      <div style={{ flex: 1 }}></div>
      <Footer />
    </Container>
    </div>
    {/* Structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "MockTestPro",
          "url": `https://mocktestpro.com/region/${currentLocation.toLowerCase()}`,
          "description": "Practice platform for competitive exams with mock tests and detailed explanations"
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": topExams.map((exam, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Course",
              "name": exam.title,
              "description": `Practice tests for ${exam.title}`,
              "provider": {
                "@type": "Organization",
                "name": "MockTestPro"
              },
              "url": `https://mocktestpro.com/exam/${exam.examId}`
            }
          }))
        })}
      </script>
    </>
  );
};

export default HomePage;
